import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

// Enums para TypeScript
export enum PortfolioMovementType {
  None = 0,
  Withdrawal = 1,
  WithdrawalReversal = 2,
  Deferral = 3,
  DeferralReversal = 4,
  ManualPunishment = 5,
  ManualUnpunishment = 6
}

export enum EnrollmentStatus {
  Active = 1,
  Withdrawn = 2,
  Deferred = 3,
  Completed = 4
}

// Interfaces para los DTOs
export interface WithdrawEnrollmentRequest {
  enrollmentId: number;
  reason: string;
  paymentPlanIds?: number[];
  notes?: string;
}

export interface ReverseWithdrawalRequest {
  enrollmentId: number;
  continueInSameProject: boolean;
  newProjectId?: number;
  reason: string;
  notes?: string;
  // Nuevos campos para valores personalizados
  newTotalValueCOP?: number;
  newTotalValueUSD?: number;
  currencyId?: number;
  newPaymentPlan?: { installmentNumber: number; dueDate: Date; amount: number; }[];
}

export interface DeferEnrollmentRequest {
  enrollmentId: number;
  reason: string;
  paymentPlanIds?: number[];
  notes?: string;
}

export interface ContinueFromDeferralRequest {
  enrollmentId: number;
  continueInSameProject: boolean;
  newProjectId?: number;
  reason: string;
  notes?: string;
  newTotalValue?: number;
  newPaymentPlan?: { installmentNumber: number; dueDate: Date; amount: number; }[];
}

export interface PendingInstallment {
  paymentPlanId: number;
  installmentNumber: number;
  dueDate: Date;
  amountToPay: number;
  amountPaid: number;
  pendingAmount: number;
  paymentStatus: string;
  paymentStatusDescription: string;
  isPunished: boolean;
  movementType: PortfolioMovementType;
  isOverdue: boolean;
  daysOverdue: number;
}

export interface EnrollmentWithdrawalInfo {
  enrollmentId: number;
  status: EnrollmentStatus;
  statusDescription: string;
  participantId: number;
  participantName: string;
  participantDocument: string;
  participantEmail: string;
  projectId: number;
  projectName: string;
  projectMnemonic: string;
  totalAmount: number;
  amountPaid: number;
  remainingBalance: number;
  currencyCode?: string;
  currencySymbol?: string;
  totalInstallments: number;
  paidInstallments: number;
  pendingInstallments: number;
  overdueInstallments: number;
  pendingPaymentPlans: PendingInstallment[];
  canBeWithdrawn: boolean;
  canBeDeferred: boolean;
  canBeReversed: boolean;
  canContinueFromDeferral: boolean;
  originEnrollmentId?: number;
  withdrawalReason?: string;
  lastPortfolioMovementDate?: Date;
}

export interface PortfolioMovementResult {
  success: boolean;
  message: string;
  movementId?: number;
  enrollmentId: number;
  newEnrollmentStatus: EnrollmentStatus;
  newEnrollmentId?: number;
  affectedInstallmentsCount: number;
  totalAffectedAmount: number;
  affectedPaymentPlanIds: number[];
  migratedPaymentPlanIds: number[];
}

export interface PortfolioMovementResponse {
  id: number;
  enrollmentId: number;
  movementType: PortfolioMovementType;
  movementTypeDescription: string;
  movementDate: Date;
  reason?: string;
  targetEnrollmentId?: number;
  paymentPlanIdsJson?: string;
  performedBy: string;
  totalAffectedAmount: number;
  affectedInstallmentsCount: number;
  notes?: string;
  isReverted: boolean;
  revertedByMovementId?: number;
  participantName?: string;
  projectName?: string;
  targetProjectName?: string;
}

export interface PortfolioMovementStats {
  totalWithdrawals: number;
  totalReversals: number;
  totalDeferrals: number;
  totalWithdrawnAmount: number;
  totalReversedAmount: number;
  totalDeferredAmount: number;
  activeWithdrawals: number;
  activeDeferrals: number;
}

@Injectable({
  providedIn: 'root'
})
export class PortfolioMovementService {
  private _baseUrl: string = "";
  private _controllerName: string = "";

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
    this._controllerName = "PortfolioMovement";
  }

  // Obtener información de una inscripción para el wizard
  getEnrollmentWithdrawalInfo(enrollmentId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/enrollment-info/${enrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  // Obtener inscripciones activas de un participante
  getActiveEnrollmentsByParticipant(participantId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/participant/${participantId}/active`)
      .pipe(catchError(this.handleErrors));
  }

  // Obtener inscripciones retiradas de un participante
  getWithdrawnEnrollmentsByParticipant(participantId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/participant/${participantId}/withdrawn`)
      .pipe(catchError(this.handleErrors));
  }

  // Obtener inscripciones aplazadas de un participante
  getDeferredEnrollmentsByParticipant(participantId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/participant/${participantId}/deferred`)
      .pipe(catchError(this.handleErrors));
  }

  // Procesar retiro
  withdrawEnrollment(request: WithdrawEnrollmentRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/withdraw`, request)
      .pipe(catchError(this.handleErrors));
  }

  // Revertir retiro
  reverseWithdrawal(request: ReverseWithdrawalRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/reverse-withdrawal`, request)
      .pipe(catchError(this.handleErrors));
  }

  // Procesar aplazamiento
  deferEnrollment(request: DeferEnrollmentRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/defer`, request)
      .pipe(catchError(this.handleErrors));
  }

  // Continuar desde aplazamiento
  continueFromDeferral(request: ContinueFromDeferralRequest): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/continue-from-deferral`, request)
      .pipe(catchError(this.handleErrors));
  }

  // 🆕 Castigar cuotas manualmente
  punishInstallments(request: { enrollmentId: number, paymentPlanIds: number[], reason: string, notes?: string }): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/punish-installments`, request)
      .pipe(catchError(this.handleErrors));
  }

  // 🆕 Descastigar cuotas manualmente
  unpunishInstallments(request: { enrollmentId: number, paymentPlanIds: number[], reason: string, notes?: string }): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/unpunish-installments`, request)
      .pipe(catchError(this.handleErrors));
  }

  // Obtener movimientos paginados (listado global)
  getMovementsPaged(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.pageNumber) httpParams = httpParams.set('pageNumber', params.pageNumber.toString());
      if (params.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
      if (params.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);
      if (params.movementType !== null && params.movementType !== undefined && params.movementType !== '')
        httpParams = httpParams.set('movementType', params.movementType.toString());
      if (params.projectId) httpParams = httpParams.set('projectId', params.projectId.toString());
      if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
      if (params.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
    }
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/list`, { params: httpParams })
      .pipe(catchError(this.handleErrors));
  }

  // Obtener historial de movimientos
  getMovementHistory(enrollmentId: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/history/${enrollmentId}`)
      .pipe(catchError(this.handleErrors));
  }

  // Obtener estadísticas
  getMovementStats(projectId?: number, fromDate?: Date, toDate?: Date): Observable<any> {
    let params = new HttpParams();
    if (projectId) params = params.set('projectId', projectId.toString());
    if (fromDate) params = params.set('fromDate', fromDate.toISOString());
    if (toDate) params = params.set('toDate', toDate.toISOString());

    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/stats`, { params })
      .pipe(catchError(this.handleErrors));
  }

  // Helper para obtener descripción del tipo de movimiento
  static getMovementTypeDescription(movementType: PortfolioMovementType): string {
    switch (movementType) {
      case PortfolioMovementType.Withdrawal: return 'Retiro';
      case PortfolioMovementType.WithdrawalReversal: return 'Reversión de Retiro';
      case PortfolioMovementType.Deferral: return 'Aplazamiento';
      case PortfolioMovementType.DeferralReversal: return 'Reversión de Aplazamiento';
      case PortfolioMovementType.ManualPunishment: return 'Castigo Manual';
      case PortfolioMovementType.ManualUnpunishment: return 'Descastigo Manual';
      default: return 'Sin movimiento';
    }
  }

  // Helper para obtener descripción del estado de inscripción
  static getEnrollmentStatusDescription(status: EnrollmentStatus): string {
    switch (status) {
      case EnrollmentStatus.Active: return 'Activa';
      case EnrollmentStatus.Withdrawn: return 'Retirada';
      case EnrollmentStatus.Deferred: return 'Aplazada';
      case EnrollmentStatus.Completed: return 'Completada';
      default: return 'Desconocido';
    }
  }

  private handleErrors(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.log("Ocurrió un error", error.error.message);
    } else {
      console.log(`Ocurrió un error ${error.status}, ${error.error}`);
    }
    return throwError(() => new Error('Algo salió mal; por favor, intente nuevamente más tarde.'));
  }
}
